// src/features/infoscreens/pages/EditInfoscreenPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  ViewList as ViewListIcon,
  ViewModule,
  AccountBox as AccountBoxIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { INFOSCREENS_LIST_PATH } from '../paths';

// Hooks
import { useInfoscreens } from '../hooks/useInfoscreens';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { FormPage, FormSection, FormActions } from '../../../components/common/form';

export default function EditInfoscreenPage() {
  const { t } = useTranslation(['infoscreens', 'common']);
  const navigate = useNavigate();
  const { infoscreenId } = useParams();
  const { showSuccess, showError } = useSnackbar();
  const { navigateBack } = useNavigationStack();

  // State
  const [infoscreen, setInfoscreen] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    login_code: '',
    viewmode: 'card',
    auto_fullscreen: true,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(true);

  // API hooks
  const {
    fetchInfoscreenById,
    updateInfoscreen,
    loading: updating,
    error,
    clearError,
  } = useInfoscreens();

  // Load infoscreen data
  useEffect(() => {
    const loadInfoscreenData = async () => {
      if (!infoscreenId) return;

      try {
        setLoading(true);
        const result = await fetchInfoscreenById(infoscreenId);
        
        if (result.success) {
          const infoscreenData = result.data;
          setInfoscreen(infoscreenData);
          setFormData({
            name: infoscreenData.name || '',
            login_code: infoscreenData.login_code || '',
            viewmode: infoscreenData.viewmode || 'card',
            auto_fullscreen: infoscreenData.auto_fullscreen ?? true,
          });
        } else {
          showError(t('infoscreens:errors.load_failed'));
        }
      } catch (error) {
        showError(t('infoscreens:errors.load_failed'));
      } finally {
        setLoading(false);
      }
    };

    loadInfoscreenData();
  }, [infoscreenId, fetchInfoscreenById, showError, t]);

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    console.log("CHANGEING", field, value)
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('infoscreens:validation.name_required');
    } else if (formData.name.length < 2) {
      errors.name = t('infoscreens:validation.name_min_length');
    } else if (formData.name.length > 255) {
      errors.name = t('infoscreens:validation.name_max_length');
    }

    if (!formData.login_code.trim()) {
      errors.login_code = t('infoscreens:validation.login_code_required');
    } else if (formData.login_code.length < 4) {
      errors.login_code = t('infoscreens:validation.login_code_min_length');
    } else if (formData.login_code.length > 255) {
      errors.login_code = t('infoscreens:validation.login_code_max_length');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm() || !infoscreen) {
      return;
    }

    try {
      const updateData = {
        name: formData.name.trim(),
        login_code: formData.login_code.trim(),
        viewmode: formData.viewmode,
        auto_fullscreen: formData.auto_fullscreen,
      };

      const result = await updateInfoscreen(infoscreen.infoscreen_id, updateData);

      if (result.success) {
        showSuccess(t('infoscreens:success.infoscreen_updated'));
        navigate(`/infoscreens/${infoscreen.infoscreen_id}`);
      } else {
        showError(result.error || t('infoscreens:errors.update_failed'));
      }
    } catch (error) {
      showError(t('infoscreens:errors.unexpected_error'));
    }
  }, [validateForm, infoscreen, formData, updateInfoscreen, showSuccess, showError, navigate, t]);

  const handleCancel = useCallback(() => {
    navigateBack(infoscreen ? `/infoscreens/${infoscreen.infoscreen_id}` : INFOSCREENS_LIST_PATH);
  }, [navigateBack, infoscreen]);

  // Loading state
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Not found state
  if (!infoscreen) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate(INFOSCREENS_LIST_PATH)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight="bold">
            {t('infoscreens:messages.infoscreen_not_found')}
          </Typography>
        </Box>
        <Alert severity="error">
          {t('infoscreens:messages.infoscreen_not_found_description')}
        </Alert>
      </Box>
    );
  }

  return (
    <FormPage
      title={t('infoscreens:edit_infoscreen')}
      subtitle={t('infoscreens:form.edit_title', { name: infoscreen.name })}
      crumb={infoscreen.name}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
      error={error}
      onErrorClose={clearError}
    >
      <FormSection title={t('infoscreens:form.edit_infoscreen_information')}>
        <TextField
          fullWidth
          label={t('infoscreens:labels.name')}
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          error={!!validationErrors.name}
          helperText={validationErrors.name || t('infoscreens:form.name_helper')}
          required
          disabled={updating}
          placeholder={t('infoscreens:placeholders.name')}
          inputProps={{ maxLength: 255 }}
        />

        <TextField
          fullWidth
          label={t('infoscreens:labels.login_code')}
          value={formData.login_code}
          onChange={(e) => handleInputChange('login_code', e.target.value)}
          error={!!validationErrors.login_code}
          helperText={validationErrors.login_code || t('infoscreens:form.login_code_helper')}
          required
          disabled={updating}
          placeholder={t('infoscreens:placeholders.login_code')}
          inputProps={{ maxLength: 255 }}
        />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('infoscreens:labels.viewmode')}
          </Typography>
          <ToggleButtonGroup
            value={formData.viewmode}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                handleInputChange('viewmode', newValue);
              }
            }}
            disabled={updating}
            sx={{ 
              mt: 1,
            }}
          >
            <ToggleButton value="card" sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 100 }}>
              <ViewModule fontSize="large" />
              <Typography variant="body2">
                {t('infoscreens:viewmodes.card')}
              </Typography>
            </ToggleButton>
            <ToggleButton value="list" sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 100 }}>
              <ViewListIcon fontSize="large" />
              <Typography variant="body2">
                {t('infoscreens:viewmodes.list')}
              </Typography>
            </ToggleButton>
            <ToggleButton value="user" sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 100 }}>
              <AccountBoxIcon fontSize="large" />
              <Typography variant="body2">
                {t('infoscreens:viewmodes.user')}
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={formData.auto_fullscreen}
              onChange={(e) => handleInputChange('auto_fullscreen', e.target.checked)}
              disabled={updating}
            />
          }
          label={
            <Box>
              <Typography variant="body2">{t('infoscreens:labels.auto_fullscreen')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('infoscreens:form.auto_fullscreen_helper')}
              </Typography>
            </Box>
          }
        />
      </FormSection>
      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={updating ? t('infoscreens:actions.updating') : t('infoscreens:actions.update_infoscreen')}
        submitIcon={<SaveIcon />}
        loading={updating}
      />
    </FormPage>
  );
}
